import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  Dimensions,
  FlatList,
  I18nManager,
  Modal,
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  ToastAndroid,
  TouchableOpacity,
  View,
} from 'react-native';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useHeaderHeight } from '@react-navigation/elements';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { alarmApi } from '../services/api';
import { alarmKeys } from '../hooks/queries';
import logger from '../utils/logger';
import { canResolveAlarms } from '../utils/permissions';
import AppToast from '../components/AppToast';
import EmptyState from '../components/EmptyState';
import ErrorView from '../components/ErrorView';
import LoadingScreen from '../components/LoadingScreen';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isSmallScreen = SCREEN_WIDTH < 375;

type AlarmStatus = 'all' | 'active' | 'acknowledged' | 'resolved';
type AlarmSeverity = 'all' | 'critical' | 'warning';

// ✅ خيارات ثابتة للسبب (عربي/إنجليزي)
const ACK_REASONS = {
  ar: [
    'تم حل المشكلة',
    'تحت المراقبة',
    'قيد الصيانة',
    'خطأ في القياس',
    'تنبيه خاطئ',
    'تم إخطار الفني',
    'في انتظار قطع الغيار',
    'سيتم الحل لاحقاً',
  ],
  en: [
    'Issue resolved',
    'Under monitoring',
    'Under maintenance',
    'Sensor error',
    'False alarm',
    'Technician notified',
    'Waiting for spare parts',
    'Will be resolved later',
  ],
};

interface Alarm {
  id: string;
  deviceId?: string;
  ruleName: string;
  message?: string;
  severity: 'critical' | 'warning' | 'info';
  status: string;
  createdAt: string;
  acknowledgedAt?: string;
  acknowledgedBy?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
  ackReason?: string;
  slaDeadlineAt?: string;
  timeRemainingMinutes?: number;
  isOverdue?: boolean;
  slaStatus?: 'pending_ack' | 'ack_on_time' | 'ack_overdue';
  currentEscalationLevel?: number;
  device?: {
    id?: string;
    name: string;
    deviceType?: string;
    hasBattery?: boolean;
    zone?: {
      name: string;
      site?: {
        name: string;
      };
    };
  };
}

export default function AlarmsScreen({ navigation, route }: any) {
  // P2: Read notification params from route
  const notificationParams = route?.params;

  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Header is transparent (see AlarmsStackNavigator) — push content below it
  const headerHeight = useHeaderHeight();

  // React Query: fetch all alarms
  const normalizeStatus = (status?: string) => (status || '').toLowerCase();

  const {
    data: rawAlarms = [],
    isLoading,
    error: queryError,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: alarmKeys.list(user?.orgId!, undefined, undefined),
    queryFn: async () => {
      const allData = await alarmApi.getAlarms(user!.orgId!, undefined);
      const alarmsArray = Array.isArray(allData) ? allData : [];
      return alarmsArray.map((alarm: any) => {
        const normalizedStatusVal = normalizeStatus(alarm.status);
        const normalizedSeverity = normalizeStatus(alarm.severity) as
          | 'critical'
          | 'warning'
          | 'info'
          | undefined;
        const zone =
          alarm?.device?.zone && typeof alarm.device.zone === 'object'
            ? alarm.device.zone
            : undefined;
        return {
          ...alarm,
          status: normalizedStatusVal || alarm.status,
          severity: (normalizedSeverity || alarm.severity) as 'critical' | 'warning' | 'info',
          ruleName:
            alarm.ruleName ||
            alarm.rule?.name ||
            alarm.type ||
            (i18n.language === 'ar' ? 'تنبيه' : 'Alarm'),
          device: alarm.device ? { ...alarm.device, zone } : alarm.device,
        } as Alarm;
      });
    },
    enabled: !!user?.orgId,
    staleTime: 15_000,
  });

  // Local state for optimistic updates overlay
  const [localAlarmOverrides, setLocalAlarmOverrides] = useState<Record<string, Partial<Alarm>>>(
    {},
  );
  const allAlarms = useMemo(
    () =>
      rawAlarms.map((alarm) =>
        localAlarmOverrides[alarm.id] ? { ...alarm, ...localAlarmOverrides[alarm.id] } : alarm,
      ),
    [rawAlarms, localAlarmOverrides],
  );

  const [filter, setFilter] = useState<AlarmStatus>('active');
  const [severityFilter, setSeverityFilter] = useState<AlarmSeverity>('critical');
  const error = queryError ? (queryError as Error).message : null;
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [expandedAlarmId, setExpandedAlarmId] = useState<string | null>(null);
  // refreshing is now isRefetching from React Query
  const [highlightedAlarmId, setHighlightedAlarmId] = useState<string | null>(
    notificationParams?.alarmId || null,
  );

  // ✅ حالة عرض التنبيهات المحدودة (20-30 للأداء)
  const [displayLimit, setDisplayLimit] = useState(20);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // ✅ حالة Modal للتفاصيل
  const [showAckModal, setShowAckModal] = useState(false);
  const [selectedAlarmId, setSelectedAlarmId] = useState<string | null>(null);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    visible: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    visible: false,
    message: '',
    type: 'info',
  });

  // ✅ حالة Modal للتصعيد (Gap 4)
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [escalateAlarmId, setEscalateAlarmId] = useState<string | null>(null);

  // Auto-scroll to highlighted alarm if present
  useEffect(() => {
    if (highlightedAlarmId && allAlarms.length > 0) {
      let clearHighlightTimeout: ReturnType<typeof setTimeout>;
      const timeout = setTimeout(() => {
        logger.log('🎯 Auto-scrolling to alarm:', highlightedAlarmId);
        clearHighlightTimeout = setTimeout(() => setHighlightedAlarmId(null), 3000);
      }, 500);
      return () => {
        clearTimeout(timeout);
        clearTimeout(clearHighlightTimeout);
      };
    }
  }, [highlightedAlarmId, allAlarms]);

  // ✨ Apply severity filter from route params (منفصل للتطبيق الفوري)
  useEffect(() => {
    if (route?.params?.severity) {
      logger.info('Applying severity filter from route params:', route.params.severity);
      setSeverityFilter(route.params.severity);
    }
  }, [route?.params?.severity]);

  useEffect(() => {
    setDisplayLimit(20);
  }, [filter, severityFilter]);

  useEffect(() => {
    const totalFiltered =
      filter === 'all'
        ? allAlarms.length
        : allAlarms.filter((alarm) => normalizeStatus(alarm.status) === filter).length;

    // ✨ تطبيق فلتر الخطورة أيضاً
    const withSeverityFilter =
      severityFilter === 'all'
        ? totalFiltered
        : allAlarms.filter((alarm) => {
            const matchesStatus = filter === 'all' || normalizeStatus(alarm.status) === filter;
            const matchesSeverity = normalizeStatus(alarm.severity) === severityFilter;
            return matchesStatus && matchesSeverity;
          }).length;

    setShowLoadMore(withSeverityFilter > displayLimit);
  }, [allAlarms, filter, severityFilter, displayLimit]);

  // ✅ الإنذارات المفلترة محلياً مع عرض محدود
  const filteredAlarms = React.useMemo(() => {
    // فلترة محلية بناءً على الحالة المحددة
    let filtered =
      filter === 'all'
        ? allAlarms
        : allAlarms.filter((alarm) => normalizeStatus(alarm.status) === filter);

    // ✨ فلترة بناءً على مستوى الخطورة
    if (severityFilter !== 'all') {
      filtered = filtered.filter((alarm) => normalizeStatus(alarm.severity) === severityFilter);
    }

    // تطبيق العرض المحدود
    return filtered.slice(0, displayLimit);
  }, [allAlarms, filter, severityFilter, displayLimit]);

  useEffect(() => {
    const isArabic = i18n.language === 'ar';
    const isRTL = I18nManager.isRTL || isArabic;

    navigation.setOptions({
      headerRight: () => (
        <TouchableOpacity
          style={{ marginRight: isRTL ? 0 : 16, marginLeft: isRTL ? 16 : 0 }}
          onPress={() => navigation.navigate('CustomTemplates')}
        >
          <Ionicons name="options-outline" size={24} color="#FFF" />
        </TouchableOpacity>
      ),
      headerLeft: () => null,
      title: isArabic ? 'التنبيهات' : 'Alarms',
    });
  }, [navigation, theme, i18n.language]);

  const onRefresh = () => {
    setLocalAlarmOverrides({});
    refetch();
  };

  // ✅ Relative time display helper
  const getRelativeTime = (dateStr: string): string => {
    const isArabic = i18n.language === 'ar';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return isArabic ? 'الآن' : 'Just now';
    if (diffMin < 60) return isArabic ? `منذ ${diffMin} د` : `${diffMin}m ago`;
    if (diffHrs < 24) return isArabic ? `منذ ${diffHrs} س` : `${diffHrs}h ago`;
    if (diffDays < 7) return isArabic ? `منذ ${diffDays} ي` : `${diffDays}d ago`;
    return date.toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
      day: '2-digit',
      month: 'short',
    });
  };

  // ✅ Gap 4: Handler للتصعيد (Escalation)
  const handleEscalate = (alarmId: string) => {
    logger.log('📈 Opening escalation modal for alarm:', alarmId);
    setEscalateAlarmId(alarmId);
    setShowEscalateModal(true);
  };

  const performEscalate = async (level: number) => {
    if (!escalateAlarmId) return;

    const isArabic = i18n.language === 'ar';

    try {
      logger.log('📈 Escalating alarm to level:', level, 'alarmId:', escalateAlarmId);

      await alarmApi.escalateAlarm(escalateAlarmId, level);

      // ✅ إغلاق Modal
      setShowEscalateModal(false);
      setEscalateAlarmId(null);

      // ✅ عرض رسالة نجاح
      showToast(isArabic ? `✓ تم التصعيد إلى المستوى ${level}` : `✓ Escalated to level ${level}`);

      // ✅ إعادة تحميل التنبيهات
      queryClient.invalidateQueries({ queryKey: alarmKeys.all });
    } catch (error: any) {
      logger.error('❌ Failed to escalate alarm:', error);

      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';

      if (Platform.OS === 'web') {
        showToast(`${isArabic ? 'خطأ' : 'Error'}: ${errorMessage}`);
      } else {
        Alert.alert(isArabic ? 'خطأ' : 'Error', errorMessage);
      }
    }
  };

  const handleAcknowledge = async (alarmId: string) => {
    logger.log('🔔 START: Acknowledge button clicked for alarm:', alarmId);

    // ✅ CHECK: Verify alarm is not already resolved or acknowledged
    const alarm = allAlarms.find((a) => a.id === alarmId);
    if (!alarm) {
      logger.warn('⚠️ Alarm not found in local state:', alarmId);
      return;
    }

    if (alarm.status === 'resolved') {
      const isArabic = i18n.language === 'ar';
      showToast(isArabic ? '⚠️ هذا التنبيه محلول بالفعل' : '⚠️ This alarm is already resolved');
      return;
    }

    if (alarm.status === 'acknowledged') {
      const isArabic = i18n.language === 'ar';
      showToast(isArabic ? 'ℹ️ هذا التنبيه مقر بالفعل' : 'ℹ️ This alarm is already acknowledged');
      return;
    }

    // ✅ فتح Modal لإدخال التفاصيل
    setSelectedAlarmId(alarmId);
    setSelectedReason('');
    setCustomReason('');
    setShowAckModal(true);
  };

  const performAcknowledge = async () => {
    if (!selectedAlarmId) return;

    const isArabic = i18n.language === 'ar';
    setIsSubmitting(true);

    try {
      // ✅ استخدام السبب المحدد أو المخصص أو الافتراضي
      const finalReason = customReason.trim()
        ? customReason.trim()
        : selectedReason
        ? selectedReason
        : isArabic
        ? 'تم الإقرار'
        : 'Acknowledged';

      logger.log(
        '🔔 Attempting to acknowledge alarm:',
        selectedAlarmId,
        'with reason:',
        finalReason,
      );
      const result = await alarmApi.acknowledgeAlarm(selectedAlarmId, finalReason);
      logger.log('✅ Alarm acknowledged successfully:', result);

      // ✅ تحديث الحالة المحلية فوراً
      // Create acknowledgedBy object from user data
      const acknowledgedByUser =
        user?.id && user?.email
          ? {
              id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
            }
          : undefined;

      setLocalAlarmOverrides((prev) => ({
        ...prev,
        [selectedAlarmId]: {
          status: 'acknowledged',
          acknowledgedAt: new Date().toISOString(),
          acknowledgedBy: acknowledgedByUser,
        },
      }));

      // ✅ إغلاق Modal
      setShowAckModal(false);
      setSelectedAlarmId(null);

      // ✅ استخدام Toast بدلاً من Alert
      showToast(isArabic ? '✓ تم إقرار الإنذار بنجاح' : '✓ Alarm acknowledged successfully');

      // ✅ Reload alarms في الخلفية لضمان المزامنة
      logger.log('🔄 Reloading alarms');
      queryClient.invalidateQueries({ queryKey: alarmKeys.all });
    } catch (error: any) {
      logger.error('❌ Failed to acknowledge alarm:', error);

      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      const statusCode = error.response?.status || 'N/A';
      logger.error('📊 Final error details:', { statusCode, errorMessage });

      // ✅ توفير رسائل واضحة للأخطاء الشائعة
      let userMessage = errorMessage;
      if (errorMessage.includes('already resolved') || errorMessage.includes('محلول')) {
        userMessage = isArabic
          ? 'هذا التنبيه محلول بالفعل. لا يمكن إقراره.'
          : 'This alarm is already resolved and cannot be acknowledged.';
        // ✅ تحديث الحالة المحلية
        queryClient.invalidateQueries({ queryKey: alarmKeys.all });
      } else if (errorMessage.includes('already acknowledged') || errorMessage.includes('مقر')) {
        userMessage = isArabic ? 'هذا التنبيه مقر بالفعل.' : 'This alarm is already acknowledged.';
        queryClient.invalidateQueries({ queryKey: alarmKeys.all });
      } else if (errorMessage.includes('not found')) {
        userMessage = isArabic ? 'التنبيه غير موجود.' : 'Alarm not found.';
      }

      // ✅ استخدام native alert للويب و Alert.alert للموبايل
      if (Platform.OS === 'web') {
        showToast(`${isArabic ? 'خطأ' : 'Error'}: ${userMessage}`);
      } else {
        Alert.alert(isArabic ? 'خطأ' : 'Error', userMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResolve = async (alarmId: string) => {
    logger.log('✅ START: Resolve button clicked for alarm:', alarmId);
    const isArabic = i18n.language === 'ar';

    // ✅ CHECK: Verify alarm is not already resolved
    const alarm = allAlarms.find((a) => a.id === alarmId);
    if (!alarm) {
      logger.warn('⚠️ Alarm not found in local state:', alarmId);
      return;
    }

    if (alarm.status === 'resolved') {
      showToast(isArabic ? '⚠️ هذا التنبيه محلول بالفعل' : '⚠️ This alarm is already resolved');
      return;
    }

    if (Platform.OS === 'web') {
      const confirmMessage = isArabic
        ? 'هل تريد حل هذا التنبيه؟'
        : 'Do you want to resolve this alarm?';
      const confirmed = window.confirm(confirmMessage);

      if (!confirmed) {
        logger.log('❌ User cancelled resolve');
        return;
      }
    } else {
      return new Promise((resolve) => {
        Alert.alert(
          isArabic ? 'حل التنبيه' : 'Resolve Alarm',
          isArabic ? 'هل تريد حل هذا التنبيه؟' : 'Do you want to resolve this alarm?',
          [
            {
              text: isArabic ? 'إلغاء' : 'Cancel',
              style: 'cancel',
              onPress: () => {
                logger.log('❌ User cancelled resolve');
                resolve(null);
              },
            },
            {
              text: isArabic ? 'حل' : 'Resolve',
              onPress: async () => {
                await performResolve(alarmId, isArabic);
                resolve(null);
              },
            },
          ],
        );
      });
    }

    await performResolve(alarmId, isArabic);
  };

  const performResolve = async (alarmId: string, isArabic: boolean) => {
    try {
      logger.log('✅ Attempting to resolve alarm:', alarmId);
      const result = await alarmApi.resolveAlarm(alarmId, 'Resolved via mobile app');
      logger.log('✅ Alarm resolved successfully:', result);

      // ✅ تحديث الحالة المحلية فوراً
      setLocalAlarmOverrides((prev) => ({
        ...prev,
        [alarmId]: {
          status: 'resolved',
          resolvedAt: new Date().toISOString(),
          resolvedBy: user?.id,
        },
      }));

      // ✅ استخدام Toast بدلاً من Alert
      showToast(isArabic ? '✓ تم حل الإنذار بنجاح' : '✓ Alarm resolved successfully');

      // ✅ Reload alarms في الخلفية لضمان المزامنة
      logger.log('🔄 Reloading alarms');
      queryClient.invalidateQueries({ queryKey: alarmKeys.all });
    } catch (error: any) {
      logger.error('❌ Failed to resolve alarm:', error);

      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      const statusCode = error.response?.status || 'N/A';
      logger.error('📊 Final error details:', { statusCode, errorMessage });

      // ✅ توفير رسائل واضحة للأخطاء الشائعة
      let userMessage = errorMessage;
      if (errorMessage.includes('already resolved') || errorMessage.includes('محلول')) {
        userMessage = isArabic ? 'هذا التنبيه محلول بالفعل.' : 'This alarm is already resolved.';
        queryClient.invalidateQueries({ queryKey: alarmKeys.all });
      } else if (errorMessage.includes('not found')) {
        userMessage = isArabic ? 'التنبيه غير موجود.' : 'Alarm not found.';
      }

      if (Platform.OS === 'web') {
        showToast(userMessage);
      } else {
        Alert.alert(isArabic ? 'خطأ' : 'Error', userMessage);
      }
    }
  };

  const getSeverityStyle = (severity: string) => {
    switch (severity) {
      case 'critical':
        return {
          bg: theme.colors.error + '20',
          text: theme.colors.error,
          icon: 'alert-circle' as const,
          label: i18n.language === 'ar' ? 'حرج' : 'Critical',
        };
      case 'warning':
        return {
          bg: theme.colors.warning + '20',
          text: theme.colors.warning,
          icon: 'warning' as const,
          label: i18n.language === 'ar' ? 'تحذير' : 'Warning',
        };
      default:
        return {
          bg: theme.colors.info + '20',
          text: theme.colors.info,
          icon: 'information-circle' as const,
          label: i18n.language === 'ar' ? 'معلومة' : 'Info',
        };
    }
  };

  const getAlarmDetails = (message: string, isArabic: boolean) => {
    if (!message) return null;

    // ✅ استخراج القيم الرقمية من الرسالة
    const tempMatch = message.match(/(\d+\.?\d*)\s*°?C/);
    const humidityMatch = message.match(/(\d+\.?\d*)\s*%/);
    const levelMatch = message.match(/(\d+\.?\d*)\s*cm/); // ✅ جديد: استخراج المنسوب
    const limitMatch = message.match(/limit:\s*(\d+\.?\d*)/);

    let details = [];

    // ✅ تفاصيل تحذيرات المنسوب (الأولوية الأعلى)
    if (levelMatch && (message.includes('level') || message.includes('منسوب'))) {
      const currentLevel = parseFloat(levelMatch[1]);
      const limitLevel = limitMatch ? parseFloat(limitMatch[1]) : null;

      if (message.includes('High level') || message.includes('منسوب مرتفع')) {
        details.push({
          icon: 'arrow-up-circle',
          label: isArabic ? 'المنسوب الحالي' : 'Current Level',
          value: `${currentLevel} ${isArabic ? 'سم' : 'cm'}`,
          color: '#FF5722',
        });
        if (limitLevel) {
          details.push({
            icon: 'swap-vertical',
            label: isArabic ? 'الحد الأقصى المسموح' : 'Maximum Limit',
            value: `${limitLevel} ${isArabic ? 'سم' : 'cm'}`,
            color: '#FF5722',
          });
        }
      } else if (message.includes('Low level') || message.includes('منسوب منخفض')) {
        details.push({
          icon: 'arrow-down-circle',
          label: isArabic ? 'المنسوب الحالي' : 'Current Level',
          value: `${currentLevel} ${isArabic ? 'سم' : 'cm'}`,
          color: '#2196F3',
        });
        if (limitLevel) {
          details.push({
            icon: 'swap-vertical',
            label: isArabic ? 'الحد الأدنى المسموح' : 'Minimum Limit',
            value: `${limitLevel} ${isArabic ? 'سم' : 'cm'}`,
            color: '#2196F3',
          });
        }
      }
      return details.length > 0 ? details : null;
    }

    // ✅ تفاصيل تحذيرات درجة الحرارة
    if (tempMatch) {
      const currentTemp = parseFloat(tempMatch[1]);
      const limitTemp = limitMatch ? parseFloat(limitMatch[1]) : null;

      if (message.includes('High') || message.includes('أعلى')) {
        details.push({
          icon: 'thermometer',
          label: isArabic ? 'درجة الحرارة الحالية' : 'Current Temperature',
          value: `${currentTemp}°${isArabic ? 'م' : 'C'}`,
          color: '#FF5722',
        });
        if (limitTemp) {
          details.push({
            icon: 'swap-vertical',
            label: isArabic ? 'الحد الأقصى المسموح' : 'Maximum Limit',
            value: `${limitTemp}°${isArabic ? 'م' : 'C'}`,
            color: '#FF5722',
          });
        }
      } else if (message.includes('Low') || message.includes('منخفضة')) {
        details.push({
          icon: 'snow',
          label: isArabic ? 'درجة الحرارة الحالية' : 'Current Temperature',
          value: `${currentTemp}°${isArabic ? 'م' : 'C'}`,
          color: '#2196F3',
        });
        if (limitTemp) {
          details.push({
            icon: 'swap-vertical',
            label: isArabic ? 'الحد الأدنى المسموح' : 'Minimum Limit',
            value: `${limitTemp}°${isArabic ? 'م' : 'C'}`,
            color: '#2196F3',
          });
        }
      }
    }

    // ✅ تفاصيل تحذيرات الرطوبة
    if (humidityMatch) {
      const currentHumidity = parseFloat(humidityMatch[1]);
      details.push({
        icon: 'water',
        label: isArabic ? 'الرطوبة الحالية' : 'Current Humidity',
        value: `${currentHumidity}%`,
        color: '#4CAF50',
      });
    }

    return details.length > 0 ? details : null;
  };

  const getAlarmIcon = (message: string, severity: string) => {
    if (!message) return 'alert-circle';

    // ✅ تحذيرات المنسوب (الخزانات والمياه والديزل والغاز) - أولوية عالية
    if (
      message.includes('level') ||
      message.includes('منسوب') ||
      message.includes('مستوى الخزان')
    ) {
      if (
        message.includes('High level') ||
        message.includes('منسوب مرتفع') ||
        message.includes('منسوب عالي')
      ) {
        return 'arrow-up-circle'; // منسوب مرتفع
      } else if (message.includes('Low level') || message.includes('منسوب منخفض')) {
        return 'arrow-down-circle'; // منسوب منخفض
      }
      return 'analytics'; // أيقونة عامة للمنسوب
    }

    // ✅ تحذيرات درجة الحرارة
    if (
      message.includes('temperature') ||
      message.includes('درجة حرارة') ||
      message.includes('حرارة')
    ) {
      if (
        message.includes('High temperature') ||
        message.includes('حرارة عالية') ||
        message.includes('أعلى')
      ) {
        return 'thermometer'; // حرارة عالية
      } else if (message.includes('Low temperature') || message.includes('حرارة منخفضة')) {
        return 'snow'; // حرارة منخفضة (ثلاجة)
      }
      return 'thermometer-outline';
    }

    // ✅ تحذيرات الرطوبة
    if (message.includes('humidity') || message.includes('رطوبة')) {
      return 'water';
    }

    // ✅ تحذيرات البطارية
    if (message.includes('battery') || message.includes('بطارية')) {
      return 'battery-dead';
    }

    // ✅ تحذيرات الاتصال
    if (message.includes('offline') || message.includes('غير متصل')) {
      return 'wifi-off';
    }

    // ✅ أيقونة افتراضية حسب الشدة
    return severity === 'critical'
      ? 'alert-circle'
      : severity === 'warning'
      ? 'warning'
      : 'information-circle';
  };

  const getAlarmTypeLabel = (message: string, isArabic: boolean) => {
    if (!message) return isArabic ? 'تنبيه عام' : 'General Alert';

    // ✅ تحذيرات المنسوب (الأولوية الأعلى)
    if (message.includes('level') || message.includes('منسوب')) {
      if (
        message.includes('High level') ||
        message.includes('منسوب مرتفع') ||
        message.includes('منسوب عالي')
      ) {
        return isArabic ? 'منسوب مرتفع' : 'High Level';
      } else if (message.includes('Low level') || message.includes('منسوب منخفض')) {
        return isArabic ? 'منسوب منخفض' : 'Low Level';
      }
      return isArabic ? 'مستوى غير طبيعي' : 'Abnormal Level';
    }

    // ✅ تحذيرات درجة الحرارة
    if (
      message.includes('temperature') ||
      message.includes('درجة حرارة') ||
      message.includes('حرارة')
    ) {
      if (
        message.includes('High temperature') ||
        message.includes('حرارة عالية') ||
        message.includes('أعلى')
      ) {
        return isArabic ? 'درجة حرارة عالية' : 'High Temperature';
      } else if (message.includes('Low temperature') || message.includes('حرارة منخفضة')) {
        return isArabic ? 'درجة حرارة منخفضة' : 'Low Temperature';
      }
      return isArabic ? 'درجة حرارة غير طبيعية' : 'Abnormal Temperature';
    }

    // ✅ تحذيرات الرطوبة
    if (message.includes('humidity') || message.includes('رطوبة')) {
      return isArabic ? 'مستوى رطوبة غير طبيعي' : 'Abnormal Humidity Level';
    }

    // ✅ تحذيرات البطارية
    if (message.includes('battery') || message.includes('بطارية')) {
      return isArabic ? 'بطارية منخفضة' : 'Low Battery';
    }

    // ✅ تحذيرات الاتصال
    if (message.includes('offline') || message.includes('غير متصل')) {
      return isArabic ? 'جهاز غير متصل' : 'Device Offline';
    }

    // ✅ رسالة افتراضية
    return message;
  };

  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (message: string) => {
    if (Platform.OS === 'android') {
      ToastAndroid.show(message, ToastAndroid.SHORT);
    }

    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ visible: true, message, type: 'info' });
    toastTimeoutRef.current = setTimeout(() => {
      setToast((prev) => ({ ...prev, visible: false }));
    }, 2500);
  };

  const renderAlarm = ({ item }: { item: Alarm }) => {
    const severityStyle = getSeverityStyle(item.severity);
    const isArabic = i18n.language === 'ar';
    const isRTL = I18nManager.isRTL || isArabic;
    const isExpanded = expandedAlarmId === item.id;
    const hasSla = typeof item.timeRemainingMinutes === 'number';
    const slaMinutes = item.timeRemainingMinutes ?? 0;
    const slaOverdue = item.isOverdue === true || item.slaStatus === 'ack_overdue';
    const slaText = hasSla
      ? slaOverdue
        ? isArabic
          ? `متأخر ${Math.abs(slaMinutes)} دقيقة`
          : `Overdue by ${Math.abs(slaMinutes)} min`
        : isArabic
        ? `متبقي ${slaMinutes} دقيقة`
        : `${slaMinutes} min remaining`
      : null;

    // ✅ الحصول على نوع التحذير والأيقونة المناسبة
    const alarmIcon = getAlarmIcon(item.message || '', item.severity);
    const alarmTypeLabel = getAlarmTypeLabel(item.message || '', isArabic);
    const alarmDetails = getAlarmDetails(item.message || '', isArabic);

    // Site · Device · Zone compact format
    const locationParts = [
      item.device?.zone?.site?.name,
      item.device?.name,
      item.device?.zone?.name,
    ].filter(Boolean);
    const locationText = locationParts.join(' · ') || (isArabic ? 'موقع غير محدد' : 'No location');

    // Determine card elevation based on severity and status
    const cardElevation =
      item.severity === 'critical' && item.status === 'active'
        ? 6
        : item.severity === 'warning' && item.status === 'active'
        ? 3
        : item.status === 'resolved'
        ? 0
        : 2;

    // ✅ P1-06: Build comprehensive accessibility label
    const severityLabel =
      item.severity === 'critical'
        ? isArabic
          ? 'تنبيه حرج'
          : 'Critical alarm'
        : isArabic
        ? 'تنبيه تحذير'
        : 'Warning alarm';
    const statusLabel =
      item.status === 'active'
        ? isArabic
          ? 'نشط'
          : 'Active'
        : item.status === 'acknowledged'
        ? isArabic
          ? 'مقر'
          : 'Acknowledged'
        : isArabic
        ? 'محلول'
        : 'Resolved';
    const timeAgo = new Date(item.createdAt).toLocaleString(isArabic ? 'ar-SA' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
    const accessibilityLabel = `${severityLabel}, ${alarmTypeLabel}, ${
      item.device?.name || ''
    }, ${statusLabel}, ${timeAgo}`;
    const accessibilityHint = isExpanded
      ? isArabic
        ? 'انقر نقرًا مزدوجًا لإخفاء التفاصيل'
        : 'Double tap to hide details'
      : isArabic
      ? 'انقر نقرًا مزدوجًا لعرض التفاصيل'
      : 'Double tap to view details';

    return (
      <TouchableOpacity
        style={[
          styles.alarmCard,
          {
            backgroundColor: theme.colors.card,
            borderLeftWidth: 4,
            borderLeftColor: severityStyle.bg,
            elevation: cardElevation,
            shadowColor: severityStyle.bg,
            shadowOffset: { width: 0, height: cardElevation / 2 },
            shadowOpacity: cardElevation > 0 ? 0.2 : 0.05,
            shadowRadius: cardElevation,
            opacity: item.status === 'resolved' ? 0.7 : 1,
          },
        ]}
        onPress={() => setExpandedAlarmId(isExpanded ? null : item.id)}
        activeOpacity={0.8}
        // ✅ P1-06: Accessibility
        accessible={true}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
        accessibilityRole="button"
        accessibilityState={{ expanded: isExpanded }}
      >
        {/* Compact Header */}
        <View style={[styles.alarmCompactHeader, false]}>
          <View style={[styles.severityIndicator, { backgroundColor: severityStyle.bg }]}>
            <Ionicons name={severityStyle.icon} size={16} color={severityStyle.text} />
          </View>

          <View style={{ flex: 1, marginHorizontal: 12 }}>
            <Text
              style={[
                styles.alarmTitleCompact,
                { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
              ]}
              numberOfLines={1}
            >
              {alarmTypeLabel}
            </Text>
            <Text
              style={[
                styles.locationTextCompact,
                { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
              ]}
              numberOfLines={1}
            >
              {locationText}
            </Text>
          </View>

          <View style={{ alignItems: isRTL ? 'flex-start' : 'flex-end' }}>
            <Text style={[styles.alarmTimeCompact, { color: theme.colors.textSecondary }]}>
              {getRelativeTime(item.createdAt)}
            </Text>
            <Ionicons
              name={isExpanded ? 'chevron-up' : 'chevron-down'}
              size={16}
              color={theme.colors.textSecondary}
              style={{ marginTop: 4 }}
            />
          </View>
        </View>

        {/* Expanded Details */}
        {isExpanded && (
          <View style={styles.expandedDetails}>
            {/* Full message if available */}
            {item.message && item.message !== 'Recovered' && item.message.trim() !== '' && (
              <View
                style={[
                  styles.messageContainer,
                  { backgroundColor: severityStyle.bg + '20' },
                  false,
                ]}
              >
                <Ionicons name="information-circle-outline" size={14} color={severityStyle.text} />
                <Text
                  style={[
                    styles.messageText,
                    { color: severityStyle.text, textAlign: isRTL ? 'right' : 'left' },
                  ]}
                >
                  {item.message}
                </Text>
              </View>
            )}

            {/* Alarm details with values */}
            {alarmDetails && alarmDetails.length > 0 && (
              <View
                style={[styles.alarmValuesContainer, { backgroundColor: theme.colors.background }]}
              >
                {alarmDetails.map((detail, index) => (
                  <View key={index} style={[styles.alarmValueItem, false]}>
                    <Ionicons name={detail.icon as any} size={16} color={detail.color} />
                    <View
                      style={{ flex: 1, marginLeft: isRTL ? 0 : 8, marginRight: isRTL ? 8 : 0 }}
                    >
                      <Text
                        style={[
                          styles.alarmValueLabel,
                          {
                            color: theme.colors.textSecondary,
                            textAlign: isRTL ? 'right' : 'left',
                          },
                        ]}
                      >
                        {detail.label}
                      </Text>
                      <Text
                        style={[
                          styles.alarmValueText,
                          { color: detail.color, textAlign: isRTL ? 'right' : 'left' },
                        ]}
                      >
                        {detail.value}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            )}

            {/* Full timestamp */}
            <View style={[styles.detailRow, false]}>
              <Ionicons name="time-outline" size={14} color={theme.colors.textSecondary} />
              <Text style={[styles.expandedDetailText, { color: theme.colors.textSecondary }]}>
                {new Date(item.createdAt).toLocaleString(isArabic ? 'ar-SA' : 'en-US', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Text>
            </View>

            {/* SLA countdown */}
            {slaText && (
              <View style={[styles.detailRow, false]}>
                <Ionicons
                  name="timer-outline"
                  size={14}
                  color={slaOverdue ? '#E53935' : theme.colors.textSecondary}
                />
                <Text
                  style={[
                    styles.expandedDetailText,
                    { color: slaOverdue ? '#E53935' : theme.colors.textSecondary },
                  ]}
                >
                  {isArabic ? `SLA: ${slaText}` : `SLA: ${slaText}`}
                </Text>
              </View>
            )}

            {/* Escalation level */}
            {item.currentEscalationLevel !== null && item.currentEscalationLevel !== undefined && (
              <View style={[styles.detailRow, false]}>
                <Ionicons
                  name="alert-circle-outline"
                  size={14}
                  color={theme.colors.textSecondary}
                />
                <Text style={[styles.expandedDetailText, { color: theme.colors.textSecondary }]}>
                  {isArabic
                    ? `تصعيد المستوى ${item.currentEscalationLevel}`
                    : `Escalation level ${item.currentEscalationLevel}`}
                </Text>
              </View>
            )}

            {/* Acknowledgment info */}
            {item.ackReason && (
              <View style={[styles.detailRow, false]}>
                <Ionicons name="chatbubble-ellipses" size={14} color={theme.colors.textSecondary} />
                <Text style={[styles.expandedDetailText, { color: theme.colors.textSecondary }]}>
                  {isArabic ? `ملاحظة: ${item.ackReason}` : `Note: ${item.ackReason}`}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Context Actions */}
        {item.status === 'active' && (
          <View style={styles.contextActionsRow}>
            <TouchableOpacity
              style={[
                styles.contextActionButton,
                { backgroundColor: theme.colors.primary, flex: 1, marginEnd: 8 },
                false,
              ]}
              onPress={() => handleAcknowledge(item.id)}
              activeOpacity={0.7}
              // ✅ P1-06: Accessibility
              accessible={true}
              accessibilityLabel={isArabic ? 'إقرار التنبيه' : 'Acknowledge alarm'}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لإقرار هذا التنبيه'
                  : 'Double tap to acknowledge this alarm'
              }
              accessibilityRole="button"
            >
              <Ionicons name="checkmark-circle" size={18} color={slaOverdue ? '#E53935' : '#fff'} />
              <Text style={[styles.contextActionText, slaOverdue && { fontWeight: '700' }]}>
                {isArabic ? 'إقرار' : 'Acknowledge'}
                {slaOverdue && ` (${Math.abs(slaMinutes)}m)`}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.contextActionButton, { backgroundColor: '#FF6B35', flex: 1 }, false]}
              onPress={() => handleEscalate(item.id)}
              activeOpacity={0.7}
              // ✅ P1-06: Accessibility
              accessible={true}
              accessibilityLabel={isArabic ? 'تصعيد التنبيه' : 'Escalate alarm'}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لتصعيد هذا التنبيه'
                  : 'Double tap to escalate this alarm'
              }
              accessibilityRole="button"
            >
              <Ionicons name="arrow-up-circle" size={18} color="#fff" />
              <Text style={styles.contextActionText}>{isArabic ? 'تصعيد' : 'Escalate'}</Text>
            </TouchableOpacity>
          </View>
        )}

        {item.status === 'acknowledged' && canResolveAlarms(user?.roles || []) && (
          <TouchableOpacity
            style={[styles.contextActionButton, { backgroundColor: theme.colors.success }, false]}
            onPress={() => handleResolve(item.id)}
            activeOpacity={0.7}
            // ✅ P1-06: Accessibility
            accessible={true}
            accessibilityLabel={isArabic ? 'حل التنبيه' : 'Resolve alarm'}
            accessibilityHint={
              isArabic
                ? 'انقر نقرًا مزدوجًا لتحديد هذا التنبيه كمحلول'
                : 'Double tap to mark this alarm as resolved'
            }
            accessibilityRole="button"
          >
            <Ionicons name="checkmark-done-circle" size={18} color="#fff" />
            <Text style={styles.contextActionText}>{isArabic ? 'حل' : 'Resolve'}</Text>
          </TouchableOpacity>
        )}

        {item.status === 'resolved' && (
          <View
            style={[styles.resolvedBadge, { backgroundColor: theme.colors.success + '15' }, false]}
          >
            <Ionicons name="checkmark-circle" size={14} color={theme.colors.success} />
            <Text style={[styles.resolvedText, { color: theme.colors.success }]}>
              {isArabic ? '✓ محلول' : '✓ Resolved'}
            </Text>
          </View>
        )}

        {/* Go to Device Button */}
        {(item.deviceId || item.device?.id) && (
          <TouchableOpacity
            style={[styles.goToDeviceButton, { borderColor: theme.colors.primary + '40' }]}
            onPress={() => {
              const devId = item.deviceId || item.device?.id;
              navigation.navigate('Devices', {
                screen: 'DeviceDetail',
                params: {
                  deviceId: devId,
                  deviceName: item.device?.name || '',
                  deviceType: item.device?.deviceType,
                  hasBattery: item.device?.hasBattery,
                },
              });
            }}
            activeOpacity={0.7}
            accessible={true}
            accessibilityLabel={
              isArabic
                ? `الذهاب للحساس: ${item.device?.name || ''}`
                : `Go to device: ${item.device?.name || ''}`
            }
            accessibilityRole="button"
          >
            <Ionicons name="hardware-chip-outline" size={16} color={theme.colors.primary} />
            <Text style={[styles.goToDeviceText, { color: theme.colors.primary }]}>
              {isArabic ? 'الذهاب للحساس' : 'Go to Device'}
            </Text>
            <Ionicons
              name={isRTL ? 'chevron-back' : 'chevron-forward'}
              size={16}
              color={theme.colors.primary}
            />
          </TouchableOpacity>
        )}
      </TouchableOpacity>
    );
  };

  const SkeletonCard = () => (
    <View
      style={[
        styles.alarmCard,
        styles.skeletonCard,
        { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
      ]}
    >
      <View style={styles.skeletonLine} />
      <View style={[styles.skeletonLine, { width: '60%', marginTop: 8 }]} />
      <View style={[styles.skeletonLine, { width: '40%', marginTop: 8 }]} />
    </View>
  );

  if (isLoading && !isRefetching && allAlarms.length === 0) {
    return (
      <View
        style={[
          styles.container,
          { backgroundColor: theme.colors.background, paddingTop: headerHeight },
        ]}
      >
        {/* Sticky Stats Bar */}
        <View style={[styles.statsBar, { backgroundColor: theme.colors.card }]}>
          <View style={styles.statItem}>
            <View style={[styles.skeletonCircle, { backgroundColor: theme.colors.border }]} />
            <View style={[styles.skeletonSmall, { backgroundColor: theme.colors.border }]} />
          </View>
          <View style={styles.statItem}>
            <View style={[styles.skeletonCircle, { backgroundColor: theme.colors.border }]} />
            <View style={[styles.skeletonSmall, { backgroundColor: theme.colors.border }]} />
          </View>
          <View style={styles.statItem}>
            <View style={[styles.skeletonCircle, { backgroundColor: theme.colors.border }]} />
            <View style={[styles.skeletonSmall, { backgroundColor: theme.colors.border }]} />
          </View>
        </View>

        {/* Sticky Filter Bar */}
        <View style={[styles.filterContainer, { backgroundColor: theme.colors.card }]}>
          {[1, 2, 3, 4].map((i) => (
            <View
              key={i}
              style={[styles.skeletonFilterTab, { backgroundColor: theme.colors.border }]}
            />
          ))}
        </View>

        {/* Skeleton Cards */}
        <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.listContent}>
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </ScrollView>
      </View>
    );
  }

  const stats = {
    active: allAlarms.filter((a) => normalizeStatus(a.status) === 'active').length,
    acknowledged: allAlarms.filter((a) => normalizeStatus(a.status) === 'acknowledged').length,
    resolved: allAlarms.filter((a) => normalizeStatus(a.status) === 'resolved').length,
    total: allAlarms.length,
  };

  const isRTL = I18nManager.isRTL || i18n.language === 'ar';

  // P1-03: Explicit state rendering
  if (isLoading && !isRefetching) {
    return (
      <LoadingScreen
        message={i18n.language === 'ar' ? 'جاري تحميل التنبيهات...' : 'Loading alarms...'}
      />
    );
  }

  if (error) {
    return (
      <ErrorView
        message={error}
        onRetry={() => refetch()}
        icon="notifications-outline"
        title={i18n.language === 'ar' ? 'فشل تحميل التنبيهات' : 'Failed to Load Alarms'}
      />
    );
  }

  // P1-03: Empty state for no alarms
  if (allAlarms.length === 0 && !isLoading && !error) {
    return (
      <EmptyState
        icon="notifications-outline"
        title={i18n.language === 'ar' ? 'لا توجد تنبيهات' : 'No Alarms'}
        message={
          i18n.language === 'ar'
            ? 'لم يتم تسجيل أي تنبيهات بعد. عندما يحدث تنبيه، سيظهر هنا.'
            : 'No alarms have been recorded yet. When an alarm occurs, it will appear here.'
        }
        actionLabel={i18n.language === 'ar' ? 'عرض الأجهزة' : 'View Devices'}
        onAction={() => navigation.navigate('Devices')}
      />
    );
  }

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: theme.colors.background, paddingTop: headerHeight },
      ]}
    >
      {/* Stats Bar */}
      <View
        style={[
          styles.statsBar,
          {
            backgroundColor: theme.colors.card,
            borderBottomColor: theme.colors.border,
          },
        ]}
      >
        <View style={[styles.statItem, { backgroundColor: theme.colors.error + '15' }]}>
          <Ionicons name="alert-circle" size={isSmallScreen ? 18 : 22} color={theme.colors.error} />
          <Text style={[styles.statValue, { color: theme.colors.error }]}>{stats.active}</Text>
          <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]} numberOfLines={1}>
            {i18n.language === 'ar' ? 'نشط' : 'Active'}
          </Text>
        </View>
        <View style={[styles.statItem, { backgroundColor: theme.colors.success + '15' }]}>
          <Ionicons
            name="checkmark-done-circle"
            size={isSmallScreen ? 18 : 22}
            color={theme.colors.success}
          />
          <Text style={[styles.statValue, { color: theme.colors.success }]}>
            {stats.acknowledged}
          </Text>
          <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]} numberOfLines={1}>
            {i18n.language === 'ar' ? 'مقر' : 'Acked'}
          </Text>
        </View>
        <View style={[styles.statItem, { backgroundColor: theme.colors.primary + '15' }]}>
          <Ionicons
            name="notifications"
            size={isSmallScreen ? 18 : 22}
            color={theme.colors.primary}
          />
          <Text style={[styles.statValue, { color: theme.colors.primary }]}>{stats.total}</Text>
          <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]} numberOfLines={1}>
            {i18n.language === 'ar' ? 'الكل' : 'Total'}
          </Text>
        </View>
      </View>

      {/* Filter Tabs - Status */}
      <View
        style={[
          styles.filterContainer,
          { backgroundColor: theme.colors.card, borderBottomColor: theme.colors.border },
        ]}
      >
        <TouchableOpacity
          style={[
            styles.filterTab,
            { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
            filter === 'active' && {
              backgroundColor: theme.colors.error,
              borderColor: theme.colors.error,
            },
          ]}
          onPress={() => setFilter('active')}
          // ✅ P1-06: Accessibility
          accessible={true}
          accessibilityLabel={`${i18n.language === 'ar' ? 'تنبيهات نشطة' : 'Active alarms'}, ${
            stats.active
          }`}
          accessibilityHint={
            i18n.language === 'ar' ? 'انقر لعرض التنبيهات النشطة' : 'Tap to show active alarms'
          }
          accessibilityRole="button"
          accessibilityState={{ selected: filter === 'active' }}
        >
          <Text
            style={[
              styles.filterText,
              { color: theme.colors.text },
              filter === 'active' && { color: '#ffffff' },
            ]}
            numberOfLines={1}
          >
            {i18n.language === 'ar' ? 'نشط' : 'Active'} ({stats.active})
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.filterTab,
            { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
            filter === 'acknowledged' && {
              backgroundColor: theme.colors.success,
              borderColor: theme.colors.success,
            },
          ]}
          onPress={() => setFilter('acknowledged')}
          // ✅ P1-06: Accessibility
          accessible={true}
          accessibilityLabel={`${
            i18n.language === 'ar' ? 'تنبيهات مقرة' : 'Acknowledged alarms'
          }, ${stats.acknowledged}`}
          accessibilityHint={
            i18n.language === 'ar'
              ? 'انقر لعرض التنبيهات المقرة'
              : 'Tap to show acknowledged alarms'
          }
          accessibilityRole="button"
          accessibilityState={{ selected: filter === 'acknowledged' }}
        >
          <Text
            style={[
              styles.filterText,
              { color: theme.colors.text },
              filter === 'acknowledged' && { color: '#ffffff' },
            ]}
            numberOfLines={1}
          >
            {i18n.language === 'ar' ? 'مقر' : 'Acked'} ({stats.acknowledged})
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.filterTab,
            { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
            filter === 'resolved' && {
              backgroundColor: theme.colors.info,
              borderColor: theme.colors.info,
            },
          ]}
          onPress={() => setFilter('resolved')}
          // ✅ P1-06: Accessibility
          accessible={true}
          accessibilityLabel={`${i18n.language === 'ar' ? 'تنبيهات محلولة' : 'Resolved alarms'}, ${
            stats.resolved
          }`}
          accessibilityHint={
            i18n.language === 'ar' ? 'انقر لعرض التنبيهات المحلولة' : 'Tap to show resolved alarms'
          }
          accessibilityRole="button"
          accessibilityState={{ selected: filter === 'resolved' }}
        >
          <Text
            style={[
              styles.filterText,
              { color: theme.colors.text },
              filter === 'resolved' && { color: '#ffffff' },
            ]}
            numberOfLines={1}
          >
            {i18n.language === 'ar' ? 'محلول' : 'Resolved'}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.filterTab,
            { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
            filter === 'all' && { backgroundColor: '#3949ab', borderColor: theme.colors.primary },
          ]}
          onPress={() => setFilter('all')}
          // ✅ P1-06: Accessibility
          accessible={true}
          accessibilityLabel={`${i18n.language === 'ar' ? 'جميع التنبيهات' : 'All alarms'}, ${
            stats.total
          }`}
          accessibilityHint={
            i18n.language === 'ar' ? 'انقر لعرض جميع التنبيهات' : 'Tap to show all alarms'
          }
          accessibilityRole="button"
          accessibilityState={{ selected: filter === 'all' }}
        >
          <Text
            style={[
              styles.filterText,
              { color: theme.colors.text },
              filter === 'all' && { color: '#ffffff' },
            ]}
            numberOfLines={1}
          >
            {i18n.language === 'ar' ? 'الكل' : 'All'} ({stats.total})
          </Text>
        </TouchableOpacity>
      </View>

      {/* Advanced Filters - Collapsible */}
      <TouchableOpacity
        style={[
          styles.advancedFilterToggle,
          { backgroundColor: theme.colors.card, borderBottomColor: theme.colors.border },
          false,
        ]}
        onPress={() => setShowAdvancedFilters(!showAdvancedFilters)}
        activeOpacity={0.7}
        // ✅ P1-06: Accessibility
        accessible={true}
        accessibilityLabel={i18n.language === 'ar' ? 'فلاتر متقدمة' : 'Advanced filters'}
        accessibilityHint={
          showAdvancedFilters
            ? i18n.language === 'ar'
              ? 'انقر لإخفاء الفلاتر المتقدمة'
              : 'Tap to hide advanced filters'
            : i18n.language === 'ar'
            ? 'انقر لعرض الفلاتر المتقدمة'
            : 'Tap to show advanced filters'
        }
        accessibilityRole="button"
        accessibilityState={{ expanded: showAdvancedFilters }}
      >
        <View style={[styles.advancedFilterHeader, false]}>
          <Ionicons name="filter" size={18} color={theme.colors.primary} />
          <Text style={[styles.advancedFilterText, { color: theme.colors.text }]}>
            {i18n.language === 'ar' ? 'فلاتر متقدمة' : 'Advanced Filters'}
          </Text>
          {severityFilter !== 'all' && (
            <View style={[styles.filterActiveBadge, { backgroundColor: theme.colors.primary }]}>
              <Text style={styles.filterActiveBadgeText}>1</Text>
            </View>
          )}
        </View>
        <Ionicons
          name={showAdvancedFilters ? 'chevron-up' : 'chevron-down'}
          size={20}
          color={theme.colors.textSecondary}
        />
      </TouchableOpacity>

      {/* Collapsible Severity Filters */}
      {showAdvancedFilters && (
        <View
          style={[
            styles.advancedFiltersContainer,
            { backgroundColor: theme.colors.card, borderBottomColor: theme.colors.border },
          ]}
        >
          <Text style={[styles.filterSectionLabel, { color: theme.colors.textSecondary }]}>
            {i18n.language === 'ar' ? 'مستوى الخطورة' : 'Severity Level'}
          </Text>
          <View style={[styles.filterContainer, false]}>
            <TouchableOpacity
              style={[
                styles.filterTab,
                { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
                severityFilter === 'critical' && {
                  backgroundColor: theme.colors.error,
                  borderColor: theme.colors.error,
                },
              ]}
              onPress={() => setSeverityFilter('critical')}
              // ✅ P1-06: Accessibility
              accessible={true}
              accessibilityLabel={
                i18n.language === 'ar' ? 'تنبيهات حرجة فقط' : 'Critical alarms only'
              }
              accessibilityRole="button"
              accessibilityState={{ selected: severityFilter === 'critical' }}
            >
              <Text
                style={[
                  styles.filterText,
                  { color: theme.colors.text },
                  severityFilter === 'critical' && { color: '#ffffff' },
                ]}
                numberOfLines={1}
              >
                {i18n.language === 'ar' ? '⚠️ حرجة' : '⚠️ Critical'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.filterTab,
                { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
                severityFilter === 'warning' && {
                  backgroundColor: theme.colors.warning,
                  borderColor: theme.colors.warning,
                },
              ]}
              onPress={() => setSeverityFilter('warning')}
              // ✅ P1-06: Accessibility
              accessible={true}
              accessibilityLabel={
                i18n.language === 'ar' ? 'تنبيهات تحذيرية فقط' : 'Warning alarms only'
              }
              accessibilityRole="button"
              accessibilityState={{ selected: severityFilter === 'warning' }}
            >
              <Text
                style={[
                  styles.filterText,
                  { color: theme.colors.text },
                  severityFilter === 'warning' && { color: '#ffffff' },
                ]}
                numberOfLines={1}
              >
                {i18n.language === 'ar' ? '⚡ تحذير' : '⚡ Warning'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.filterTab,
                { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
                severityFilter === 'all' && {
                  backgroundColor: '#3949ab',
                  borderColor: theme.colors.primary,
                },
              ]}
              onPress={() => setSeverityFilter('all')}
              // ✅ P1-06: Accessibility
              accessible={true}
              accessibilityLabel={
                i18n.language === 'ar' ? 'كل مستويات الخطورة' : 'All severity levels'
              }
              accessibilityRole="button"
              accessibilityState={{ selected: severityFilter === 'all' }}
            >
              <Text
                style={[
                  styles.filterText,
                  { color: theme.colors.text },
                  severityFilter === 'all' && { color: '#ffffff' },
                ]}
                numberOfLines={1}
              >
                {i18n.language === 'ar' ? 'الكل' : 'All'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Error State with Retry */}
      {error && !isLoading && (
        <View style={[styles.errorBanner, { backgroundColor: theme.colors.error + '15' }]}>
          <Ionicons name="alert-circle" size={20} color={theme.colors.error} />
          <Text style={[styles.errorText, { color: theme.colors.error }]}>{error}</Text>
          <TouchableOpacity
            style={[styles.retryButton, { backgroundColor: theme.colors.error }]}
            onPress={() => refetch()}
          >
            <Ionicons name="refresh" size={16} color="#fff" />
            <Text style={styles.retryButtonText}>{i18n.language === 'ar' ? 'إعادة' : 'Retry'}</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Alarms List */}
      <FlatList
        data={filteredAlarms}
        renderItem={renderAlarm}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContent}
        // ✅ P1-08: Performance optimizations for smooth scrolling
        removeClippedSubviews={Platform.OS === 'android'} // Android optimization
        initialNumToRender={15} // Render enough to fill screen + buffer
        maxToRenderPerBatch={10} // Batch size for rendering
        windowSize={21} // Number of items to render outside visible area
        updateCellsBatchingPeriod={50} // Batch UI updates every 50ms
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={onRefresh}
            colors={[theme.colors.primary]}
          />
        }
        ListFooterComponent={(() => {
          // حساب عدد التنبيهات المفلترة
          const totalFiltered =
            filter === 'all'
              ? allAlarms.length
              : allAlarms.filter((a) => a.status === filter).length;

          if (isLoadingMore) {
            return (
              <View style={{ padding: 20 }}>
                <ActivityIndicator size="small" color={theme.colors.primary} />
              </View>
            );
          }

          return showLoadMore && displayLimit < totalFiltered ? (
            <TouchableOpacity
              style={[
                styles.loadMoreButton,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
              onPress={() => {
                setIsLoadingMore(true);
                setTimeout(() => {
                  setDisplayLimit((prev) => prev + 20);
                  setIsLoadingMore(false);
                }, 300);
              }}
            >
              <Ionicons name="chevron-down-circle-outline" size={20} color={theme.colors.primary} />
              <Text style={[styles.loadMoreText, { color: theme.colors.primary }]}>
                {i18n.language === 'ar'
                  ? `عرض المزيد (${totalFiltered - displayLimit} متبقي)`
                  : `Load More (${totalFiltered - displayLimit} remaining)`}
              </Text>
            </TouchableOpacity>
          ) : null;
        })()}
        ListEmptyComponent={
          <View style={styles.emptyState}>
            {filter === 'active' && severityFilter === 'critical' ? (
              <>
                <View
                  style={[
                    styles.emptyIconContainer,
                    { backgroundColor: theme.colors.success + '15' },
                  ]}
                >
                  <Ionicons name="shield-checkmark" size={64} color={theme.colors.success} />
                </View>
                <Text style={[styles.emptyText, { color: theme.colors.text }]}>
                  {i18n.language === 'ar'
                    ? 'لا توجد تنبيهات حرجة نشطة 🎉'
                    : 'No active critical alarms 🎉'}
                </Text>
                <Text style={[styles.emptySubtext, { color: theme.colors.textSecondary }]}>
                  {i18n.language === 'ar'
                    ? 'جميع الأنظمة تعمل بشكل طبيعي'
                    : 'All systems operating normally'}
                </Text>
              </>
            ) : (
              <>
                <View
                  style={[
                    styles.emptyIconContainer,
                    { backgroundColor: theme.colors.primary + '15' },
                  ]}
                >
                  <Ionicons
                    name="checkmark-done-circle-outline"
                    size={64}
                    color={theme.colors.textSecondary}
                  />
                </View>
                <Text style={[styles.emptyText, { color: theme.colors.text }]}>
                  {i18n.language === 'ar' ? 'لا توجد إنذارات' : 'No Alarms'}
                </Text>
                <Text style={[styles.emptySubtext, { color: theme.colors.textSecondary }]}>
                  {filter !== 'all' || severityFilter !== 'all'
                    ? i18n.language === 'ar'
                      ? 'جرب تغيير الفلاتر'
                      : 'Try adjusting filters'
                    : i18n.language === 'ar'
                    ? 'اسحب للتحديث'
                    : 'Pull down to refresh'}
                </Text>
              </>
            )}
          </View>
        }
      />

      {/* ✅ Modal لإضافة تفاصيل الإقرار */}
      <Modal
        visible={showAckModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowAckModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowAckModal(false)}
        >
          <View
            style={[styles.modalContent, { backgroundColor: theme.colors.background }]}
            onStartShouldSetResponder={() => true}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {i18n.language === 'ar' ? 'تفاصيل الإقرار' : 'Acknowledgment Details'}
              </Text>
              <TouchableOpacity onPress={() => setShowAckModal(false)}>
                <Ionicons name="close-circle" size={28} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <Text style={[styles.modalSubtitle, { color: theme.colors.textSecondary }]}>
              {i18n.language === 'ar'
                ? 'اختر سببًا أو أدخل تفاصيل مخصصة (اختياري)'
                : 'Select a reason or enter custom details (optional)'}
            </Text>

            <ScrollView style={styles.modalScroll} showsVerticalScrollIndicator={false}>
              {/* ✅ خيارات الأسباب الثابتة */}
              <View style={styles.reasonsContainer}>
                {(i18n.language === 'ar' ? ACK_REASONS.ar : ACK_REASONS.en).map((reason, index) => (
                  <TouchableOpacity
                    key={index}
                    style={[
                      styles.reasonButton,
                      {
                        backgroundColor:
                          selectedReason === reason
                            ? theme.colors.primary + '20'
                            : theme.colors.card,
                        borderColor:
                          selectedReason === reason ? theme.colors.primary : theme.colors.border,
                      },
                    ]}
                    onPress={() => {
                      setSelectedReason(reason);
                      setCustomReason(''); // مسح النص المخصص عند اختيار سبب ثابت
                    }}
                  >
                    <Ionicons
                      name={selectedReason === reason ? 'checkbox' : 'square-outline'}
                      size={20}
                      color={
                        selectedReason === reason
                          ? theme.colors.primary
                          : theme.colors.textSecondary
                      }
                    />
                    <Text
                      style={[
                        styles.reasonText,
                        {
                          color:
                            selectedReason === reason ? theme.colors.primary : theme.colors.text,
                          fontWeight: selectedReason === reason ? '600' : '400',
                        },
                      ]}
                    >
                      {reason}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* ✅ حقل النص المخصص */}
              <Text style={[styles.customLabel, { color: theme.colors.textSecondary }]}>
                {i18n.language === 'ar' ? 'أو أدخل سببًا مخصصًا:' : 'Or enter a custom reason:'}
              </Text>
              <TextInput
                style={[
                  styles.customInput,
                  {
                    backgroundColor: theme.colors.card,
                    borderColor: customReason.trim() ? theme.colors.primary : theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                placeholder={i18n.language === 'ar' ? 'اكتب السبب هنا...' : 'Type reason here...'}
                placeholderTextColor={theme.colors.textSecondary}
                value={customReason}
                onChangeText={(text) => {
                  setCustomReason(text);
                  if (text.trim()) {
                    setSelectedReason(''); // مسح السبب الثابت عند الكتابة
                  }
                }}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </ScrollView>

            {/* ✅ أزرار الإجراءات */}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[
                  styles.modalButton,
                  styles.cancelButton,
                  { backgroundColor: theme.colors.card },
                ]}
                onPress={() => setShowAckModal(false)}
                disabled={isSubmitting}
              >
                <Text style={[styles.buttonText, { color: theme.colors.text }]}>
                  {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.modalButton,
                  styles.submitButton,
                  { backgroundColor: '#3949ab', opacity: isSubmitting ? 0.6 : 1 },
                ]}
                onPress={performAcknowledge}
                disabled={isSubmitting}
              >
                <Text style={[styles.buttonText, { color: '#FFF' }]}>
                  {isSubmitting
                    ? i18n.language === 'ar'
                      ? 'جاري الإقرار...'
                      : 'Acknowledging...'
                    : i18n.language === 'ar'
                    ? 'إقرار'
                    : 'Acknowledge'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* ✅ Gap 4: Modal للتصعيد */}
      <Modal
        visible={showEscalateModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowEscalateModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowEscalateModal(false)}
        >
          <View
            style={[styles.modalContent, { backgroundColor: theme.colors.background }]}
            onStartShouldSetResponder={() => true}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {i18n.language === 'ar' ? 'تصعيد التنبيه' : 'Escalate Alarm'}
              </Text>
              <TouchableOpacity onPress={() => setShowEscalateModal(false)}>
                <Ionicons name="close-circle" size={28} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <Text style={[styles.modalSubtitle, { color: theme.colors.textSecondary }]}>
              {i18n.language === 'ar'
                ? 'اختر مستوى التصعيد لإخطار المسؤولين'
                : 'Select escalation level to notify responsible personnel'}
            </Text>

            <View style={styles.escalationLevels}>
              {/* Level 1 */}
              <TouchableOpacity
                style={[
                  styles.escalationButton,
                  { backgroundColor: '#FFA72620', borderColor: '#FFA726' },
                ]}
                onPress={() => performEscalate(1)}
              >
                <View style={styles.escalationIconContainer}>
                  <Ionicons name="arrow-up-circle" size={28} color="#FFA726" />
                  <Text style={[styles.escalationLevel, { color: '#FFA726' }]}>
                    {i18n.language === 'ar' ? 'المستوى 1' : 'Level 1'}
                  </Text>
                </View>
                <Text style={[styles.escalationDescription, { color: theme.colors.text }]}>
                  {i18n.language === 'ar' ? 'مشرفي المنطقة' : 'Zone Supervisors'}
                </Text>
                <Text style={[styles.escalationNote, { color: theme.colors.textSecondary }]}>
                  {i18n.language === 'ar'
                    ? 'للمشاكل التي تحتاج انتباه فوري'
                    : 'For issues requiring immediate attention'}
                </Text>
              </TouchableOpacity>

              {/* Level 2 */}
              <TouchableOpacity
                style={[
                  styles.escalationButton,
                  { backgroundColor: '#FF7043' + '20', borderColor: '#FF7043' },
                ]}
                onPress={() => performEscalate(2)}
              >
                <View style={styles.escalationIconContainer}>
                  <Ionicons name="arrow-up-circle" size={28} color="#FF7043" />
                  <Text style={[styles.escalationLevel, { color: '#FF7043' }]}>
                    {i18n.language === 'ar' ? 'المستوى 2' : 'Level 2'}
                  </Text>
                </View>
                <Text style={[styles.escalationDescription, { color: theme.colors.text }]}>
                  {i18n.language === 'ar' ? 'مديري الموقع' : 'Site Managers'}
                </Text>
                <Text style={[styles.escalationNote, { color: theme.colors.textSecondary }]}>
                  {i18n.language === 'ar'
                    ? 'للمشاكل الحرجة غير المحلولة'
                    : 'For critical unresolved issues'}
                </Text>
              </TouchableOpacity>

              {/* Level 3 */}
              <TouchableOpacity
                style={[
                  styles.escalationButton,
                  { backgroundColor: '#E53935' + '20', borderColor: '#E53935' },
                ]}
                onPress={() => performEscalate(3)}
              >
                <View style={styles.escalationIconContainer}>
                  <Ionicons name="arrow-up-circle" size={28} color="#E53935" />
                  <Text style={[styles.escalationLevel, { color: '#E53935' }]}>
                    {i18n.language === 'ar' ? 'المستوى 3' : 'Level 3'}
                  </Text>
                </View>
                <Text style={[styles.escalationDescription, { color: theme.colors.text }]}>
                  {i18n.language === 'ar' ? 'الإدارة العليا' : 'Top Management'}
                </Text>
                <Text style={[styles.escalationNote, { color: theme.colors.textSecondary }]}>
                  {i18n.language === 'ar' ? 'للحالات الطارئة فقط' : 'For emergency situations only'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>

      <AppToast visible={toast.visible} message={toast.message} type={toast.type} bottom={18} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    fontSize: 16,
    marginTop: 16,
    textAlign: 'center',
  },
  statsBar: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
    borderBottomWidth: 1,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  statItem: {
    flex: 1,
    minWidth: 80,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  filterContainer: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
    borderBottomWidth: 1,
  },
  filterTab: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 20,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 36,
  },
  filterText: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  listContent: {
    padding: 12,
    paddingBottom: 100,
  },
  alarmCard: {
    padding: 14,
    borderRadius: 14,
    marginBottom: 10,
    borderWidth: 1,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 4,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.12)',
  },
  skeletonCard: {
    elevation: 0,
    shadowOpacity: 0,
    boxShadow: 'none',
  },
  alarmHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    flexWrap: 'wrap',
    gap: 8,
  },
  severityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 14,
    gap: 5,
  },
  severityText: {
    fontSize: 12,
    fontWeight: '700',
  },
  alarmTime: {
    fontSize: 11,
    fontWeight: '500',
  },
  alarmTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
    lineHeight: 22,
  },
  // ✅ نمط جديد لعرض نوع التحذير
  alarmTypeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    paddingVertical: 8,
  },
  // ✅ حاوية قيم التحذيرات
  alarmValuesContainer: {
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    gap: 8,
  },
  alarmValueItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  alarmValueLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 2,
  },
  alarmValueText: {
    fontSize: 14,
    fontWeight: '700',
  },
  messageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    marginBottom: 10,
    gap: 6,
  },
  messageText: {
    fontSize: 13,
    fontWeight: '600',
    flex: 1,
  },
  alarmDetails: {
    gap: 6,
    marginBottom: 10,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  alarmDevice: {
    fontSize: 13,
    flex: 1,
  },
  alarmZone: {
    fontSize: 13,
    flex: 1,
  },
  ackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    gap: 8,
    minHeight: 44,
  },
  resolveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    gap: 8,
    minHeight: 44,
  },
  ackButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  acknowledgedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 12,
    gap: 6,
    minHeight: 40,
  },
  acknowledgedText: {
    fontSize: 12,
    fontWeight: '600',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  emptyIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubtext: {
    fontSize: 13,
    textAlign: 'center',
    lineHeight: 20,
  },
  // ✅ Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: '80%',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.25)',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  modalSubtitle: {
    fontSize: 14,
    marginBottom: 16,
    lineHeight: 20,
  },
  modalScroll: {
    maxHeight: '65%',
  },
  reasonsContainer: {
    gap: 10,
    marginBottom: 16,
  },
  reasonButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 10,
    borderWidth: 2,
    gap: 10,
  },
  reasonText: {
    fontSize: 15,
    flex: 1,
  },
  loadMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1,
    gap: 8,
    marginHorizontal: 16,
    marginVertical: 12,
  },
  loadMoreText: {
    fontSize: 14,
    fontWeight: '600',
  },
  customLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  customInput: {
    borderWidth: 2,
    borderRadius: 10,
    padding: 12,
    fontSize: 15,
    minHeight: 80,
    marginBottom: 16,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    borderWidth: 1,
    borderColor: '#ccc',
  },
  submitButton: {
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  // New UX improvement styles
  alarmCompactHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  severityIndicator: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  alarmTitleCompact: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
  },
  locationTextCompact: {
    fontSize: 12,
    opacity: 0.8,
  },
  alarmTimeCompact: {
    fontSize: 11,
  },
  expandedDetails: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.05)',
  },
  expandedDetailText: {
    fontSize: 13,
    marginLeft: 6,
  },
  contextActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginTop: 12,
    gap: 6,
  },
  contextActionText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  resolvedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    marginTop: 12,
    gap: 4,
  },
  resolvedText: {
    fontSize: 13,
    fontWeight: '600',
  },
  advancedFilterToggle: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  advancedFilterHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  advancedFilterText: {
    fontSize: 14,
    fontWeight: '600',
  },
  filterActiveBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 6,
  },
  filterActiveBadgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: 'bold',
  },
  advancedFiltersContainer: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  filterSectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    marginHorizontal: 12,
    marginTop: 8,
    borderRadius: 8,
    gap: 8,
  },
  errorText: {
    flex: 1,
    fontSize: 13,
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    gap: 4,
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  skeletonLine: {
    height: 14,
    backgroundColor: 'rgba(0,0,0,0.08)',
    borderRadius: 4,
    width: '100%',
  },
  skeletonCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
  },
  skeletonSmall: {
    width: 40,
    height: 12,
    borderRadius: 4,
    marginTop: 4,
  },
  skeletonFilterTab: {
    flex: 1,
    height: 36,
    borderRadius: 20,
  },
  // ✅ Gap 4: Styles for actions row and escalation modal
  contextActionsRow: {
    flexDirection: 'row',
    marginTop: 12,
    gap: 8,
  },
  goToDeviceButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    marginTop: 10,
    borderWidth: 1,
    gap: 6,
  },
  goToDeviceText: {
    fontSize: 13,
    fontWeight: '600',
  },
  escalationLevels: {
    marginTop: 16,
    gap: 12,
  },
  escalationButton: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    gap: 8,
  },
  escalationIconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  escalationLevel: {
    fontSize: 18,
    fontWeight: '700',
  },
  escalationDescription: {
    fontSize: 15,
    fontWeight: '600',
    marginTop: 4,
  },
  escalationNote: {
    fontSize: 12,
    marginTop: 2,
  },
});
function setShowEscalateModal(arg0: boolean) {
  throw new Error('Function not implemented.');
}
